import { parseEntityRef } from "@backstage/catalog-model";
import { Content, InfoCard, MarkdownContent } from "@backstage/core-components";
import { EntityRefLink } from "@backstage/plugin-catalog-react";
import { type TemplateEntityV1beta3 } from "@backstage/plugin-scaffolder-common";
import { Box, Flex, Text } from "@backstage/ui";

type Props = { entity: TemplateEntityV1beta3 };

/**
 * Renders an access restriction notice for a scaffolder template marked as experimental.
 *
 * Shows the template title and optional description. Displays an informational
 * message indicating the template is experimental and restricted to its owners,
 * with an {@link EntityRefLink} pointing to the template owner.
 *
 * @param props - {@link Props}
 */
export function ExperimentalTemplateNotice({ entity }: Props) {
    const { title = entity.metadata.name, description } = entity.metadata;
    // EntityRefLink applies defaultKind only to its label, not its URL, so
    // shorthand owners such as "banno/nebula" must be qualified first.
    const ownerRef = entity.spec.owner
        ? parseEntityRef(entity.spec.owner, {
              defaultKind: "Group",
              defaultNamespace: "default",
          })
        : undefined;

    return (
        <Content>
            <InfoCard
                title={`🧪 EXPERIMENTAL - ${title}`}
                subheader={
                    description ? (
                        <MarkdownContent
                            content={description}
                            linkTarget="_blank"
                        />
                    ) : undefined
                }
                noPadding
                titleTypographyProps={{ component: "h2" }}
            >
                <Box p="8">
                    <Flex direction="column" gap="4">
                        <Text variant="body-medium">
                            This template is experimental and access is
                            restricted to its owners.
                        </Text>
                        {ownerRef && (
                            <Text variant="body-medium">
                                Reach out to{" "}
                                <EntityRefLink
                                    entityRef={ownerRef}
                                    defaultKind="Group"
                                />{" "}
                                for access.
                            </Text>
                        )}
                    </Flex>
                </Box>
            </InfoCard>
        </Content>
    );
}
